import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const STATE_KEY = 'mx_music_state_v1';
const SOURCE_KEY = 'mx_music_source_v1';
const LOCAL_MODE_KEY = 'mx_music_local_mode_v1';
const MUTED_AUTOPLAY_KEY = 'mx_music_muted_autoplay_v1';

const clamp01 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
};

const safeParseJson = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const formatTime = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

const GlobalMusicPlayer = ({ className = '', isOpen, onOpenChange, onUiStateChange, compactMode = false }) => {
  const audioRef = useRef(null);
  const progressTimerRef = useRef(null);
  const lastLoadErrorRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [playlist, setPlaylist] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [openUncontrolled, setOpenUncontrolled] = useState(false);

  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [source, setSource] = useState('app'); // preferred source: app | local
  const [activeSource, setActiveSource] = useState('app'); // actual loaded playlist: app | local
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [localMode, setLocalMode] = useState('folder'); // folder | single
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [autoplayMuted, setAutoplayMuted] = useState(false);
  const [directoryPickerSupported, setDirectoryPickerSupported] = useState(false);

  const currentTrack = useMemo(() => playlist[idx] || null, [playlist, idx]);

  const open = typeof isOpen === 'boolean' ? isOpen : openUncontrolled;
  const setOpen = (next) => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(!!next);
      return;
    }
    setOpenUncontrolled(!!next);
  };

  const loadMutedAutoplay = () => {
    try {
      if (typeof window === 'undefined') return true;
      const raw = window.localStorage.getItem(MUTED_AUTOPLAY_KEY);
      return raw !== '0';
    } catch {
      return true;
    }
  };

  const saveMutedAutoplay = (enabled) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(MUTED_AUTOPLAY_KEY, enabled ? '1' : '0');
    } catch {
      // ignore
    }
  };

  const loadLocalMode = () => {
    try {
      if (typeof window === 'undefined') return 'folder';
      const raw = window.localStorage.getItem(LOCAL_MODE_KEY);
      return raw === 'single' ? 'single' : 'folder';
    } catch {
      return 'folder';
    }
  };

  const saveLocalMode = (nextMode) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(LOCAL_MODE_KEY, nextMode === 'single' ? 'single' : 'folder');
    } catch {
      // ignore
    }
  };

  const loadSource = () => {
    try {
      if (typeof window === 'undefined') return 'app';
      const raw = window.localStorage.getItem(SOURCE_KEY);
      return raw === 'local' ? 'local' : 'app';
    } catch {
      return 'app';
    }
  };

  const saveSource = (nextSource) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(SOURCE_KEY, nextSource === 'local' ? 'local' : 'app');
    } catch {
      // ignore
    }
  };

  const loadState = () => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STATE_KEY) : null;
      const st = raw ? safeParseJson(raw) : null;
      if (!st || typeof st !== 'object') return null;
      return st;
    } catch {
      return null;
    }
  };

  const saveState = (next) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(STATE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const syncProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    progressTimerRef.current = setInterval(() => {
      const a = audioRef.current;
      if (!a) return;
      if (!Number.isFinite(a.duration)) return;
      setCurTime(a.currentTime || 0);
      setDuration(a.duration || 0);
    }, 500);
  };

  const stopProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const switchTrackOnList = async (list, nextIdx, opts = {}) => {
    const { autoplay = true, seekTime = null } = opts;

    if (!Array.isArray(list) || list.length === 0) return;

    const normalized = ((nextIdx % list.length) + list.length) % list.length;
    const nextTrack = list[normalized];
    if (!nextTrack || !nextTrack.url) return;

    const prevAudio = audioRef.current;
    if (prevAudio) {
      try {
        prevAudio.pause();
      } catch {
        // ignore
      }
    }

    const a = new Audio(nextTrack.url);
    try {
      a.setAttribute('playsinline', '');
      a.setAttribute('webkit-playsinline', '');
    } catch {
      // ignore
    }
    a.volume = clamp01(volume);
    a.preload = 'auto';
    a.loop = list.length <= 1;

    a.addEventListener('loadedmetadata', () => {
      setDuration(a.duration || 0);
      if (seekTime != null && Number.isFinite(Number(seekTime))) {
        try {
          a.currentTime = Math.max(0, Math.min(a.duration || 0, Number(seekTime)));
        } catch {
          // ignore
        }
      }
    });

    a.addEventListener('play', () => {
      setPlaying(true);
      syncProgressTimer();
    });

    a.addEventListener('pause', () => {
      setPlaying(false);
    });

    a.addEventListener('ended', () => {
      if (a.loop) {
        return;
      }
      switchTrack(normalized + 1, { autoplay: true, seekTime: 0 });
    });

    audioRef.current = a;
    try {
      if (typeof window !== 'undefined') {
        window.__mx_music_audio__ = a;
      }
    } catch {
      // ignore
    }
    setIdx(normalized);
    setCurTime(0);

    saveState({ idx: normalized, time: seekTime || 0, playing: !!autoplay, volume: clamp01(volume) });

    if (autoplay) {
      try {
        await a.play();
        setAutoplayBlocked(false);
      } catch {
        // Autoplay puede fallar hasta que el usuario interactúe.
        setAutoplayBlocked(true);
        try {
          if (loadMutedAutoplay()) {
            await tryPlayMuted();
          }
        } catch {
          // ignore
        }
      }
    }
  };

  const switchTrack = async (nextIdx, opts = {}) => switchTrackOnList(playlist, nextIdx, opts);

  const tryPlayCurrent = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      setAutoplayBlocked(false);
      const st = loadState() || {};
      saveState({
        ...st,
        idx,
        time: a.currentTime || 0,
        playing: true,
        volume: clamp01(volume)
      });
    } catch {
      setAutoplayBlocked(true);
    }
  };

  const tryPlayMuted = async () => {
    const a = audioRef.current;
    if (!a) return false;

    try {
      a.muted = true;
      await a.play();
      setAutoplayBlocked(false);
      setAutoplayMuted(true);
      saveMutedAutoplay(true);
      return true;
    } catch {
      try {
        a.muted = false;
      } catch {
        // ignore
      }
      setAutoplayBlocked(true);
      setAutoplayMuted(false);
      return false;
    }
  };

  const unmuteAndRestore = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.muted = false;
      a.volume = clamp01(volume);
      setAutoplayMuted(false);
      saveMutedAutoplay(false);
      if (a.paused) {
        await a.play();
      }
    } catch {
      // ignore
    }
  };

  const handleTogglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;

    if (a.paused) {
      try {
        await a.play();
        setAutoplayBlocked(false);
      } catch {
        setAutoplayBlocked(true);
      }
    } else {
      try {
        a.pause();
      } catch {
        // ignore
      }
    }

    const st = loadState() || {};
    saveState({
      ...st,
      idx,
      time: a.currentTime || 0,
      playing: !a.paused,
      volume: clamp01(volume)
    });
  };

  const handleSeek = (value01) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const t = Math.max(0, Math.min(a.duration, value01 * a.duration));
    try {
      a.currentTime = t;
      setCurTime(t);
    } catch {
      // ignore
    }
  };

  const loadPlaylist = async () => {
    const res = await axios.get('/api/music/playlist', { params: { _ts: Date.now() } });
    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    setPlaylist(items);
    setActiveSource('app');
    return items;
  };

  const applyLocalFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    const items = list
      .filter((f) => {
        const t = String(f?.type || '').toLowerCase();
        return t.startsWith('audio/');
      })
      .map((f, i) => {
        const url = URL.createObjectURL(f);
        const name = String(f?.name || `track-${i + 1}`);
        const title = name.replace(/\.[a-z0-9]+$/i, '');
        return { id: `local-${i}-${name}`, title, artist: 'Tu música', url };
      });

    setPlaylist(items);
    setActiveSource('local');
    setIdx(0);
    setCurTime(0);
    setDuration(0);

    if (items.length > 0) {
      await switchTrackOnList(items, 0, { autoplay: false, seekTime: 0 });
    }
  };

  const openLocalPicker = (mode) => {
    const nextMode = mode === 'single' ? 'single' : 'folder';
    const effectiveMode = nextMode === 'folder' && !directoryPickerSupported ? 'folder' : nextMode;
    setLocalMode(effectiveMode);
    saveLocalMode(effectiveMode);

    if (nextMode === 'single') {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
      return;
    }

    if (directoryPickerSupported && folderInputRef.current) {
      folderInputRef.current.value = '';
      folderInputRef.current.click();
      return;
    }

    // Fallback: multi-file picker
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    try {
      if (typeof document === 'undefined') return;
      const input = document.createElement('input');
      setDirectoryPickerSupported('webkitdirectory' in input);
    } catch {
      setDirectoryPickerSupported(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const initialSource = loadSource();
        setSource(initialSource);

        const initialLocalMode = loadLocalMode();
        setLocalMode(initialLocalMode);

        const initialMutedAutoplay = loadMutedAutoplay();
        setAutoplayMuted(false);
        saveMutedAutoplay(initialMutedAutoplay);

        // Siempre cargamos MundoXYZ por defecto (autoselección).
        // Si el usuario prefiere "Tu música", no podemos rehidratar archivos locales tras refresh,
        // así que reproducimos MundoXYZ hasta que el usuario elija archivos.
        lastLoadErrorRef.current = null;
        const items = await loadPlaylist();
        if (!mounted) return;

        if (items.length === 0) return;

        const st = loadState();
        const stIdx = st && typeof st.idx === 'number' ? st.idx : 0;
        const stTime = st && typeof st.time === 'number' ? st.time : 0;
        const stVol = st && st.volume != null ? st.volume : 0.8;
        const stPlaying = st ? st.playing === true : true;

        setVolume(clamp01(stVol));

        await switchTrackOnList(items, stIdx, { autoplay: stPlaying, seekTime: stTime });

        // Workaround autoplay: si el navegador bloquea audio con sonido,
        // intentamos iniciar en mute (usualmente permitido).
        if (initialMutedAutoplay) {
          const a = audioRef.current;
          if (a && a.paused) {
            await tryPlayMuted();
          }
        }
      } catch {
        lastLoadErrorRef.current = 'playlist_error';
      }
    };

    init();

    return () => {
      mounted = false;
      stopProgressTimer();
      const a = audioRef.current;
      if (a) {
        try {
          a.pause();
        } catch {
          // ignore
        }
      }
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = clamp01(volume);

    const st = loadState() || {};
    saveState({
      ...st,
      idx,
      time: a.currentTime || 0,
      playing,
      volume: clamp01(volume)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume]);

  useEffect(() => {
    if (typeof onUiStateChange !== 'function') return;
    onUiStateChange({ playing, volume: clamp01(volume), hasTrack: !!currentTrack, source: activeSource });
  }, [playing, volume, currentTrack, onUiStateChange, activeSource]);

  useEffect(() => {
    if (!open) return;
    if (!currentTrack) return;

    // Al abrir el panel (gesto del usuario), intentamos iniciar reproducción.
    // Esto evita que quede "pegado" en paused por políticas de autoplay.
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) return;

    tryPlayCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, currentTrack]);

  const hasTrack = !!currentTrack;
  if (!open && !hasTrack) return null;

  const audioPaused = !!audioRef.current?.paused;
  const canShowActivate = open && hasTrack && autoplayBlocked && audioPaused;
  const canShowUnmute = open && hasTrack && autoplayMuted;
  const shouldShowPreferredLocalNotice = open && activeSource === 'app' && source === 'local';

  const progress01 = duration > 0 ? curTime / duration : 0;

  const content = (
    <div className={`card-glass rounded-t-2xl sm:rounded-2xl p-4 text-xs ${compactMode ? '' : 'absolute left-0 right-0 bottom-0'} ${className}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold truncate">
                    {currentTrack?.title || 'Reproductor'}
                  </div>
                  <button
                    type="button"
                    className="text-[10px] text-text/60 truncate hover:text-text"
                    title="Seleccionar fuente de música"
                    onClick={() => setShowSourcePicker(true)}
                  >
                    {activeSource === 'local' ? 'Tu música' : 'MundoXYZ'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-[11px] text-text/60 hover:text-text"
                >
                  Cerrar
                </button>
              </div>

              {canShowActivate && (
                <div className="mt-3 p-3 rounded-xl bg-black/20 border border-white/10">
                  <div className="text-[11px] text-text/70">
                    Tu navegador bloqueó el autoplay. Presiona para activar.
                  </div>
                  <button
                    type="button"
                    className="mt-2 w-full px-3 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30"
                    onClick={tryPlayCurrent}
                  >
                    Activar reproducción
                  </button>
                </div>
              )}

              {shouldShowPreferredLocalNotice && (
                <div className="mt-3 p-3 rounded-xl bg-black/20 border border-white/10">
                  <div className="text-[11px] text-text/70">
                    Tu preferencia es <span className="font-semibold">Tu música</span>, pero tras recargar no se
                    pueden restaurar archivos locales. Reproduciendo <span className="font-semibold">MundoXYZ</span>
                    hasta que elijas archivos.
                  </div>
                  <button
                    type="button"
                    className="mt-2 w-full px-3 py-2 rounded-lg bg-glass hover:bg-glass-hover"
                    onClick={() => openLocalPicker(localMode)}
                  >
                    Elegir archivos
                  </button>
                </div>
              )}

              {canShowUnmute && (
                <div className="mt-3 p-3 rounded-xl bg-black/20 border border-white/10">
                  <div className="text-[11px] text-text/70">
                    Reproduciendo en silencio por política del navegador.
                  </div>
                  <button
                    type="button"
                    className="mt-2 w-full px-3 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30"
                    onClick={unmuteAndRestore}
                  >
                    Activar sonido
                  </button>
                </div>
              )}

              {showSourcePicker && (
                <div className="mt-3 p-3 rounded-xl bg-black/20 border border-white/10">
                  <div className="text-[11px] font-semibold">Fuente de sonido</div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`px-3 py-2 rounded-lg ${
                        source === 'app' ? 'bg-white/10' : 'bg-glass hover:bg-glass-hover'
                      }`}
                      onClick={async () => {
                        setSource('app');
                        saveSource('app');
                        setShowSourcePicker(false);
                        try {
                          const items = await loadPlaylist();
                          if (items.length > 0) {
                            await switchTrack(0, { autoplay: true, seekTime: 0 });
                          }
                        } catch {
                          lastLoadErrorRef.current = 'playlist_error';
                        }
                      }}
                    >
                      MundoXYZ
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-2 rounded-lg ${
                        source === 'local' ? 'bg-white/10' : 'bg-glass hover:bg-glass-hover'
                      }`}
                      onClick={() => {
                        setSource('local');
                        saveSource('local');
                        setShowSourcePicker(false);
                        openLocalPicker(localMode);
                      }}
                    >
                      Tu música
                    </button>
                  </div>

                  {source === 'local' && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        className={`px-3 py-2 rounded-lg ${
                          localMode === 'folder' ? 'bg-white/10' : 'bg-glass hover:bg-glass-hover'
                        }`}
                        onClick={() => openLocalPicker('folder')}
                        title="Elegir carpeta con varias canciones"
                        disabled={!directoryPickerSupported}
                      >
                        Carpeta
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-2 rounded-lg ${
                          localMode === 'single' ? 'bg-white/10' : 'bg-glass hover:bg-glass-hover'
                        }`}
                        onClick={() => openLocalPicker('single')}
                        title="Elegir una canción"
                      >
                        Canción
                      </button>
                    </div>
                  )}

                  {source === 'local' && !directoryPickerSupported && (
                    <div className="mt-2 text-[10px] text-text/60">
                      En este navegador (p.ej. Telegram/Android) la selección de carpeta puede no estar
                      disponible. Usa <span className="font-semibold">Canción</span> o el selector múltiple.
                    </div>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                multiple={localMode !== 'single'}
                style={{ display: 'none' }}
                onChange={async (e) => {
                  try {
                    await applyLocalFiles(e.target.files);
                  } catch {
                    // ignore
                  }
                }}
              />

              <input
                ref={folderInputRef}
                type="file"
                accept="audio/*"
                multiple
                webkitdirectory=""
                directory=""
                style={{ display: 'none' }}
                onChange={async (e) => {
                  try {
                    await applyLocalFiles(e.target.files);
                  } catch {
                    // ignore
                  }
                }}
              />

              {!hasTrack ? (
                <div className="mt-3 space-y-3">
                  <div className="text-[11px] text-text/70">
                    {source === 'local'
                      ? 'Selecciona archivos de audio para reproducir.'
                      : 'No hay canciones en la lista.'}
                  </div>
                  <div className="text-[10px] text-text/60">
                    {source === 'local' ? (
                      <span>
                        Selecciona <span className="font-semibold">Carpeta</span> o{' '}
                        <span className="font-semibold">Canción</span>. Por limitación del navegador, debes
                        elegir los archivos de nuevo si recargas la app.
                      </span>
                    ) : (
                      <span>
                        Agrega archivos .mp3 en <span className="font-semibold">backend/music/</span>.
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="w-full px-3 py-2 rounded-lg bg-glass hover:bg-glass-hover"
                    onClick={async () => {
                      try {
                        lastLoadErrorRef.current = null;
                        if (source === 'local') {
                          openLocalPicker(localMode);
                        } else {
                          const items = await loadPlaylist();
                          if (items.length > 0) {
                            await switchTrack(0, { autoplay: false, seekTime: 0 });
                          }
                        }
                      } catch {
                        lastLoadErrorRef.current = 'playlist_error';
                      }
                    }}
                  >
                    {source === 'local' ? 'Elegir archivos' : 'Reintentar'}
                  </button>
                  {lastLoadErrorRef.current && (
                    <div className="text-[10px] text-red-300">Error cargando playlist</div>
                  )}
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => switchTrack(idx - 1, { autoplay: true, seekTime: 0 })}
                      className="px-3 py-2 rounded-lg bg-glass hover:bg-glass-hover"
                      title="Anterior"
                    >
                      ⏮
                    </button>
                    <button
                      type="button"
                      onClick={handleTogglePlay}
                      className="flex-1 px-3 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30"
                      title="Play/Pause"
                    >
                      {playing ? 'Pausar' : 'Play'}
                    </button>
                    <button
                      type="button"
                      onClick={() => switchTrack(idx + 1, { autoplay: true, seekTime: 0 })}
                      className="px-3 py-2 rounded-lg bg-glass hover:bg-glass-hover"
                      title="Siguiente"
                    >
                      ⏭
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-[10px] text-text/60 w-10 text-right">{formatTime(curTime)}</div>
                    <input
                      type="range"
                      min={0}
                      max={1000}
                      value={Math.floor(progress01 * 1000)}
                      onChange={(e) => handleSeek(Number(e.target.value) / 1000)}
                      className="w-full"
                    />
                    <div className="text-[10px] text-text/60 w-10">{formatTime(duration)}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-[10px] text-text/60">Vol</div>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={volume}
                      onChange={(e) => setVolume(clamp01(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div className="max-h-56 overflow-auto border-t border-white/10 pt-3">
                    {playlist.map((t, i) => (
                      <button
                        key={t.id || `${i}`}
                        type="button"
                        onClick={() => switchTrack(i, { autoplay: true, seekTime: 0 })}
                        className={`w-full text-left px-2 py-2 rounded-lg hover:bg-white/5 ${
                          i === idx ? 'bg-white/5' : ''
                        }`}
                      >
                        <div className="text-[11px] font-semibold truncate">{t.title || '—'}</div>
                        <div className="text-[10px] text-text/60 truncate">{t.artist || ''}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
  );
  if (compactMode) return content;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm">
            {content}
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalMusicPlayer;
